import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
	Button,
	DateFieldFormik,
	FormSkeleton,
	PhoneFieldFormik,
	RadioField,
	Status,
	TextFieldFormik,
	Tooltip,
} from '@components';
import { AccountContext, ModalContext } from '@context';
import { UserAccountService } from '@data';
import { Close, Done, Edit } from '@public';
import { AccountType, AddressSearchFormValues, AddressType, UserContactFormValues } from '@types';
import {
	alternatePhoneRegExp,
	aussieMobilePhoneRegExp,
	emailRegExp,
	FormEditability,
	FormLabels,
	isDirtyFormOnTrim,
	mapAddress,
	mapUserAccountContactToFormValues,
	mapUserContactFormValuesToModel,
	toSentenceCase,
} from '@utils';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { ApiError, ApiService } from '@/services/api/ApiService';

// Styles
import styles from './userAccountContactDetails.module.scss';

const contactDetailsFormLabels: FormLabels<UserContactFormValues> = {
	firstName: 'First name',
	middleName: 'Middle name',
	lastName: 'Last name',
	preferredContactPhone: 'Preferred phone',
	mobile: 'Mobile phone',
	alternatePhone: 'Alternate phone',
	streetAddress: 'Street address',
	postalAddress: 'Postal address',
	emailAddress: 'Email address',
	emailVerified: '',
	birthDate: 'Date of birth',
};

const formValuesEditability: FormEditability<UserContactFormValues> = {
	firstName: false,
	middleName: false,
	lastName: false,
	preferredContactPhone: true,
	mobile: true,
	alternatePhone: true,
	streetAddress: true,
	postalAddress: true,
	emailAddress: true,
	emailVerified: false,
	birthDate: false,
};

const contactDetailsValidationSchema = Yup.object().shape({
	firstName: Yup.string().nullable().optional(),
	middleName: Yup.string().nullable().optional(),
	lastName: Yup.string().nullable().optional(),
	alternatePhone: Yup.string().when('preferredContactPhone', {
		is: 'AlternatePhone',
		then: schema =>
			schema
				.required('Alternate phone is required')
				.matches(alternatePhoneRegExp, 'Alternate phone must be empty or between 8 and 18 digits'),
		otherwise: schema =>
			schema.nullable().optional().matches(alternatePhoneRegExp, {
				message: 'Alternate phone must be empty or between 8 and 18 digits',
				excludeEmptyString: true,
			}),
	}),
	mobile: Yup.string()
		.matches(aussieMobilePhoneRegExp, 'Invalid mobile phone number')
		.when('preferredContactPhone', {
			is: 'Mobile',
			then: schema => schema.required('Mobile phone is required'),
			otherwise: schema => schema.nullable().optional(),
		}),
	preferredContactPhone: Yup.string().required('Please choose a preferred contact phone number'),
	streetAddress: Yup.object()
		.shape({
			postalCode: Yup.string().required('Street address is required'),
		})
		.required(),
	postalAddress: Yup.object()
		.shape({
			postalCode: Yup.string().required('Postal address is required'),
		})
		.required(),
	emailAddress: Yup.string()
		.matches(emailRegExp, 'Email address is not valid')
		.required('Email address is required'),
	emailVerified: Yup.boolean().isTrue('Email address has to be verified'),
	birthDate: Yup.string().optional().nullable(),
});

interface UserAccountContactDetailsProps {
	/**
	 * Optional form activation handler
	 */
	onActiveForm?: (isActive: boolean) => void;
}

export const UserAccountContactDetails = ({ onActiveForm }: UserAccountContactDetailsProps) => {
	// Context
	const { getUserAccount } = useContext(AccountContext);
	const {
		setShowAddressModal,
		addressTypeModal,
		setAddressTypeModal,
		setStreetAddress: setStreetAddressModal,
		setPostalAddress: setPostalAddressModal,
		addressFormValuesOnSave,
		setAddressFormValuesOnSave,
		emailAddressFromModal,
		setEmailAddressFromModal,
		setEmailAddress: setEmailAddressModal,
		setShowEmailAddressModal,
	} = useContext(ModalContext);

	// State
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [hasSetDataFromModal, setHasSetDataFromModal] = useState(false);
	const [contactDetailsOriginalValues, setContactDetailsOriginalValues] =
		useState<UserContactFormValues>(mapUserAccountContactToFormValues(getUserAccount.data));
	const [contactDetailsInitialValues, setContactDetailsInitialValues] =
		useState<UserContactFormValues>(mapUserAccountContactToFormValues(getUserAccount.data));
	const [showSuccess, setShowSuccess] = useState(false);
	const [editErrorMessage, setEditErrorMessage] = useState<string>('');
	const contactDetailsFormikRef = useRef<FormikProps<UserContactFormValues>>(null);

	// Data
	const updateUserAccount = useMutation({
		mutationFn: (partialUserAccount: Partial<AccountType>) =>
			UserAccountService.updateUserAccount(partialUserAccount),
		onSuccess: async () => await getUserAccount.refetch(),
	});

	// Initialization
	useEffect(() => {
		if (getUserAccount.data) {
			const userAcountFormValues = mapUserAccountContactToFormValues(getUserAccount.data);
			setContactDetailsOriginalValues({ ...userAcountFormValues });
			setContactDetailsInitialValues({ ...userAcountFormValues });
		}
	}, [getUserAccount.data]);

	// Emit form's edit mode
	useEffect(() => {
		if (onActiveForm) {
			onActiveForm(isEditing);
		}
	}, [isEditing, onActiveForm]);

	const isLoadingUserAccount = () => {
		return getUserAccount.isLoading || !getUserAccount.data;
	};

	const submitForm = async (values: UserContactFormValues) => {
		if (getUserAccount.data) {
			setContactDetailsInitialValues(values); // keep inputed values on form while submitting

			const userAccountUpdates: Partial<AccountType> = mapUserContactFormValuesToModel(
				values,
				formValuesEditability,
			);

			await updateUserAccount
				.mutateAsync(userAccountUpdates)
				.then(() => {
					setShowSuccess(true);
					setIsEditing(false);
				})
				.catch((error: ApiError) => {
					const errorMessage = ApiService.getErrorMessage(error);
					if (ApiService.getErrorStatusCode(error) === 400 && errorMessage)
						setEditErrorMessage(toSentenceCase(errorMessage));
					else
						setEditErrorMessage('Submission failed due to server error. Please try again later.');
				});
		}
	};

	const isEditable = (field: string) => {
		return formValuesEditability[field as keyof UserContactFormValues] ?? false;
	};

	const activateForm = () => {
		setIsEditing(true);
		setHasSetDataFromModal(false);
		setShowSuccess(false);
		setEditErrorMessage('');
		updateUserAccount.reset();
	};

	const cancelForm = () => {
		setIsEditing(false);
		setEditErrorMessage('');
		setContactDetailsInitialValues({ ...contactDetailsOriginalValues });
		updateUserAccount.reset();
	};

	const openAddressModal = async (addressType: AddressType, values: UserContactFormValues) => {
		setAddressTypeModal(addressType);
		setStreetAddressModal(values.streetAddress);
		setPostalAddressModal(values.postalAddress);
		setShowAddressModal(true);

		setContactDetailsInitialValues(values); // keep inputed values on form while submitting
	};

	const openEmailAddressModal = async (values: UserContactFormValues) => {
		setEmailAddressModal(values.emailAddress);
		setShowEmailAddressModal(true);

		setContactDetailsInitialValues(values); // keep inputed values on form while submitting
	};

	// Life cycle
	useEffect(() => {
		setShowAddressModal(false); // hide modal if was shown on a previous navigation
	}, [setShowAddressModal]);

	// Life cycle
	useEffect(() => {
		setShowEmailAddressModal(false); // hide modal if was shown on a previous navigation
	}, [setShowEmailAddressModal]);

	// Set child form (from address modal) on parent form
	useEffect(() => {
		const onSaveAddressModal = (addressSearchForm: AddressSearchFormValues) => {
			if (contactDetailsFormikRef.current && addressSearchForm.address) {
				if (addressTypeModal == AddressType.STREET) {
					contactDetailsFormikRef.current.values.streetAddress = addressSearchForm.address; // set streetAddress on form
				}
				if (
					addressSearchForm.isOverwrittingPostalAddress ||
					addressTypeModal == AddressType.POSTAL
				) {
					contactDetailsFormikRef.current.values.postalAddress = addressSearchForm.address; // set postalAddress on form
				}

				// update values on form for rendering it again with them
				setContactDetailsInitialValues({
					...contactDetailsInitialValues,
					streetAddress: contactDetailsFormikRef.current.values.streetAddress,
					postalAddress: contactDetailsFormikRef.current.values.postalAddress,
				});
				setHasSetDataFromModal(true); // make the form dirty
			}
		};

		if (addressFormValuesOnSave) {
			const values = { ...addressFormValuesOnSave };
			setAddressFormValuesOnSave(undefined);
			onSaveAddressModal(values);
		}
	}, [
		addressFormValuesOnSave,
		addressTypeModal,
		contactDetailsInitialValues,
		setAddressFormValuesOnSave,
	]);

	// Set child data (from email address modal) on parent form
	useEffect(() => {
		if (emailAddressFromModal) {
			const tempEmail = emailAddressFromModal;
			setEmailAddressFromModal(undefined);
			// update values on form for rendering it again with them
			setContactDetailsInitialValues({
				...contactDetailsInitialValues,
				emailAddress: tempEmail,
				emailVerified: true,
			});
			setHasSetDataFromModal(true); // make the form dirty
		}
	}, [emailAddressFromModal, setEmailAddressFromModal, contactDetailsInitialValues]);

	const AddressEditIcon = ({
		addressType,
		values,
		hasError,
	}: {
		addressType: AddressType;
		values: UserContactFormValues;
		hasError?: boolean;
	}) => {
		return (
			<Edit
				customStyles={[styles['edit-icon'], styles[`edit-icon-${hasError ? 'error' : ''}`]].join(
					' ',
				)}
				handleClick={() => openAddressModal(addressType, values)}
			/>
		);
	};

	const EmailAddressEditIcon = ({
		values,
		hasError,
	}: {
		values: UserContactFormValues;
		hasError?: boolean;
	}) => {
		return (
			<Edit
				customStyles={[styles['edit-icon'], styles[`edit-icon-${hasError ? 'error' : ''}`]].join(
					' ',
				)}
				handleClick={() => openEmailAddressModal(values)}
			/>
		);
	};

	const MobileTooltip = () => {
		return (
			<Tooltip
				title={'Mobile phone'}
				text={`The mobile phone number should be an Australian mobile number. 
					   If you have an international contact number, please enter it in the 'Alternate phone' field.`}
				id="roles-tooltip"
			/>
		);
	};

	const StatusComponent = () => {
		return showSuccess || editErrorMessage ? (
			<div className={styles.status}>
				<Status
					status={showSuccess ? 'success' : 'error'}
					show={true}
					onClose={() => {
						setShowSuccess(false);
						setEditErrorMessage('');
					}}
					title={showSuccess ? 'Success' : undefined}
					msg={showSuccess ? 'Updated details have been successfully saved.' : editErrorMessage}
				/>
			</div>
		) : (
			<></>
		);
	};

	const ContactDetailsForm = () => (
		<>
			<Formik
				initialValues={contactDetailsInitialValues}
				validationSchema={contactDetailsValidationSchema}
				onSubmit={submitForm}
				innerRef={contactDetailsFormikRef}
			>
				{(
					{
						dirty,
						submitForm,
						values,
						errors,
						touched,
						getFieldHelpers,
					}: FormikProps<UserContactFormValues>,
					isDisabled = !isEditing || updateUserAccount.isPending,
				) => (
					<Form>
						{isLoadingUserAccount() ? (
							<FormSkeleton labels={contactDetailsFormLabels} />
						) : (
							<div
								className={['form--grid', updateUserAccount.isPending ? 'form--frozen' : ''].join(
									' ',
								)}
							>
								<>
									<label>{contactDetailsFormLabels.firstName}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'firstName',
											placeholder: 'E.g. Mark',
											disabled: isDisabled || !isEditable('firstName'),
										}}
										size="small"
									/>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.middleName}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'middleName',
											placeholder: 'E.g. John',
											disabled: isDisabled || !isEditable('middleName'),
										}}
										size="small"
									/>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.lastName}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'lastName',
											placeholder: 'E.g. Scott',
											disabled: isDisabled || !isEditable('lastName'),
										}}
										size="small"
									/>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.mobile}:</label>
									<div className={styles['radio-buttons-container']}>
										<PhoneFieldFormik
											customStyles={styles['form-field']}
											defaultCountry="au"
											forceCountry={true}
											props={{
												name: 'mobile',
												disabled: isDisabled || !isEditable('mobile'),
												hidden: isDisabled && values.mobile == '',
											}}
										/>
										<div className={styles['radio-buttons-padding']}></div>
										<RadioField
											value="Mobile"
											hidden={isDisabled && values.preferredContactPhone != 'Mobile'}
											isChecked={values.preferredContactPhone == 'Mobile'}
											disabled={isDisabled || !isEditable('preferredContactPhone')}
											onChange={(value: string) =>
												getFieldHelpers('preferredContactPhone').setValue(value)
											}
											customStyles={[styles.cell, styles['cell-half-left'], styles.radioField].join(
												' ',
											)}
										>
											Preferred
										</RadioField>
									</div>
									{isEditing ? <MobileTooltip /> : <p></p>}
								</>
								<>
									<label>{contactDetailsFormLabels.alternatePhone}:</label>
									<div className={styles['radio-buttons-container']}>
										<PhoneFieldFormik
											customStyles={styles['form-field']}
											defaultCountry="au"
											props={{
												name: 'alternatePhone',
												disabled: isDisabled || !isEditable('alternatePhone'),
												hidden: isDisabled && values.alternatePhone == '',
											}}
										/>
										<div className={styles['radio-buttons-padding']}></div>
										<RadioField
											value="AlternatePhone"
											isChecked={values.preferredContactPhone == 'AlternatePhone'}
											hidden={isDisabled && values.preferredContactPhone != 'AlternatePhone'}
											disabled={isDisabled || !isEditable('preferredContactPhone')}
											onChange={(value: string) =>
												getFieldHelpers('preferredContactPhone').setValue(value)
											}
											customStyles={[styles.cell, styles['cell-half-left'], styles.radioField].join(
												' ',
											)}
										>
											Preferred
										</RadioField>
									</div>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.streetAddress}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'streetAddress.postalCode',
											disabled: true,
										}}
										isLocked={true}
										onLockedText={mapAddress(values.streetAddress)}
									/>
									{isEditing ? (
										<AddressEditIcon
											addressType={AddressType.STREET}
											values={values}
											hasError={Boolean(
												// eslint-disable-next-line
												// @ts-ignore // Formik detects type as string instead of Address
												errors.streetAddress?.postalCode && touched.streetAddress?.postalCode,
											)}
										/>
									) : (
										<p></p>
									)}
								</>
								<>
									<label>{contactDetailsFormLabels.postalAddress}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'postalAddress.postalCode',
											disabled: true,
										}}
										isLocked={true}
										onLockedText={mapAddress(values.postalAddress)}
									/>
									{isEditing ? (
										<AddressEditIcon
											addressType={AddressType.POSTAL}
											values={values}
											hasError={Boolean(
												// eslint-disable-next-line
												// @ts-ignore // Formik detects type as string instead of Address
												errors.postalAddress?.postalCode && touched.postalAddress?.postalCode,
											)}
										/>
									) : (
										<p></p>
									)}
								</>
								<>
									<label>{contactDetailsFormLabels.emailAddress}:</label>
									<div className={styles['text-row']}>
										<div className={[styles.text, styles['text-width']].join(' ')}>
											{values.emailAddress}
										</div>
										<div>
											{values.emailVerified ? (
												<div className={[styles.text, styles['text-verified']].join(' ')}>
													<Done customStyles={[styles.icon, styles['icon-verified']].join(' ')} />
													Verified
												</div>
											) : (
												<div className={[styles.text, styles['text-not-verified']].join(' ')}>
													<Close
														customStyles={[styles.icon, styles['icon-not-verified']].join(' ')}
													/>
													Not verified
												</div>
											)}
										</div>
									</div>
									{isEditing ? (
										<EmailAddressEditIcon
											values={values}
											hasError={Boolean(
												(errors.emailAddress && touched.emailAddress) ||
													(errors.emailVerified && touched.emailVerified),
											)}
										/>
									) : (
										<p></p>
									)}
								</>
								<>
									<label>{contactDetailsFormLabels.birthDate}:</label>
									<DateFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'birthDate',
											disabled: isDisabled || !isEditable('birthDate'),
										}}
										size="small"
									/>
									<p></p>
								</>
							</div>
						)}
						<StatusComponent />
						<NavigationButtons
							dirty={isDirtyFormOnTrim(dirty, contactDetailsInitialValues, values)}
							submitForm={submitForm}
						/>
					</Form>
				)}
			</Formik>
		</>
	);

	const NavigationButtons = ({
		dirty,
		submitForm,
	}: {
		dirty: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		submitForm: (() => Promise<void>) & (() => Promise<any>);
	}) => (
		<div className={styles['buttons-container']}>
			{isEditing && (
				<Button
					customStyles={styles['cancel-button']}
					key="cancel"
					type="secondary"
					size="small"
					onClick={cancelForm}
					disabled={updateUserAccount.isPending}
					data-testid="cancel"
				>
					Cancel
				</Button>
			)}
			<Button
				key={'update'}
				type="primary"
				size="large"
				onClick={() => {
					if (!isEditing) {
						activateForm();
					} else {
						submitForm();
					}
				}}
				isLoading={updateUserAccount.isPending}
				disabled={
					(!isEditing && isLoadingUserAccount()) ||
					(isEditing &&
						!dirty &&
						!hasSetDataFromModal &&
						!updateUserAccount.isPending &&
						!updateUserAccount.isError)
				}
				data-testid="update"
			>
				{!isEditing ? 'Edit contact details' : 'Save contact details'}
			</Button>
		</div>
	);

	return <ContactDetailsForm />;
};
